/* eslint-disable react-hooks/rules-of-hooks */
import React, { useState, useEffect, useRef } from "react";
import "./index.css";
import {
  Step3Props,
  MenuItemOption,
  CartAllocationData,
} from "../../../../../redux/AllocationRequest";
import { useLazyGetMenuItemsListQuery } from "../../../../../redux/services/MenusService";
import { useLazyGetMenuItemsByRestaurantIdQuery } from "../../../../../redux/services/menuItemService";
import Select from "react-select";
import { useSelector } from "react-redux";
import { Row, Col, Label } from "reactstrap";
import { customFilterOption } from "../../../../../utility/Utility";
import { useLazyGetCartsForRestaurantByRestaurantIdQuery } from "../../../../../redux/services/restaurantServices";
import { MenuItem } from "../../../../../redux/menuSlice";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSliders } from "@fortawesome/free-solid-svg-icons";
import CustomItemSetupModal from "../../../../Orders/CreateOrder/CollectItemsStep/CustomItemSetupModal";
import { BulkCartAllocationGrid } from "./BulkAllocationGrid";


interface CartOption {
  value: string;
  label: React.ReactNode;
  displayName: string;
  cartId: string;
  cartType: string;
  cartState: string;
  supportedChannels: string[];
  location: any;
  owner?: any;
  address?: string;
  abbreviation?: string;
}

interface PendingAllocation {
  product: MenuItemOption;
  channels: Record<string, number>;
  customItems?: any[];
  customItemTotal?: number;
}

interface CustomizedCustom {
  availableDays: any;
  priceType?: string;
  sections: any;
  totalItems: number;
  isAvailable?: boolean;
  id: string;
  displayName?: string;
  price: any;
  collectedItems: any[];
}

export const Step3: React.FC<Step3Props> = ({
  items,
  onAdd,
  onRemove,
  restaurantId,
  requestType,
  onCartAllocationsChange,
  initialCartAllocations,
  bulkGridData,
  bulkCustomData,
  onBulkGridChange,
  onBulkCustomChange,
  onBulkProductsLoaded,
  onBulkCartsLoaded,
}: any) => {
  const [selectedProduct, setSelectedProduct] = useState<MenuItemOption | null>(null);
  const [selectedCart, setSelectedCart] = useState<CartOption | null>(null);
  const [quantity, setQuantity] = useState<number>(1);
  const [overridePrice, setOverridePrice] = useState<string>("");
  const [menuItemsOptions, setMenuItemsOptions] = useState<MenuItemOption[]>([]);
  const [cartOptions, setCartOptions] = useState<CartOption[]>([]);
  const [itemError, setItemError] = useState<string | null>(null);
  const [isLoadingItems, setIsLoadingItems] = useState(false);
  const [isLoadingCarts, setIsLoadingCarts] = useState(false);
  const [cartAllocations, setCartAllocations] = useState<Map<string, CartAllocationData>>(new Map());
  const [pendingByCart, setPendingByCart] = useState<Map<string, PendingAllocation[]>>(new Map());
  const [showCustomModal, setShowCustomModal] = useState(false);
  const [selectedCustomItem, setSelectedCustomItem] = useState<MenuItem | undefined>(undefined);
  const [customizedCustomItem, setCustomizedCustomItem] = useState<CustomizedCustom | undefined>(undefined);
  const [customModalContext, setCustomModalContext] = useState<string>("");
  const [singleMenuItemsList, setSingleMenuItemsList] = useState<MenuItem[]>([]);
  const [modalOrderItems, setModalOrderItems] = useState<any[]>([]);

  const customModalContextRef = useRef<string>("");
  const selectedCustomItemRef = useRef<MenuItem | undefined>(undefined);

  const [triggerMenuItems] = useLazyGetMenuItemsListQuery();
  const [triggerCarts] = useLazyGetCartsForRestaurantByRestaurantIdQuery();
  const [menuItemTrigger] = useLazyGetMenuItemsByRestaurantIdQuery();

  const restaurantIdFromStore = useSelector(
    (state: any) => state.restaurant.selectedRestaurantDetails?.id,
  );
  const effectiveRestaurantId = restaurantId || restaurantIdFromStore;

  // ─── If Bulk Cart Allocation mode render the grid ─────────────────────────
  if (requestType === "Bulk Cart Allocation") {
    return (
      <div className="step3-container">
        
        <BulkCartAllocationGrid
          restaurantId={effectiveRestaurantId}
          gridData={bulkGridData || {}}
          customData={bulkCustomData || {}}
          onGridChange={onBulkGridChange || (() => {})}
          onCustomChange={onBulkCustomChange || (() => {})}
          onProductsLoaded={onBulkProductsLoaded}
          onCartsLoaded={onBulkCartsLoaded}
        />
      </div>
    );
  }

  const isAllocatedOrPendingForCart = (itemId: string, cartId: string) => {
    const cartAllocation = cartAllocations.get(cartId);
    if (cartAllocation?.allocations.some((a) => a.itemId === itemId)) return true;
    const pendingRows = pendingByCart.get(cartId) || [];
    if (pendingRows.some((r) => r.product.itemId === itemId)) return true;
    return false;
  };

  const isProductUsedInAnyCart = (itemId: string, currentCartId?: string) => {
    for (const [cartId, cart] of cartAllocations) {
      if (cartId !== currentCartId && cart.allocations.some((a) => a.itemId === itemId)) return true;
    }
    for (const [cartId, rows] of pendingByCart) {
      if (cartId !== currentCartId && rows.some((r) => r.product.itemId === itemId)) return true;
    }
    return false;
  };

  const isAddToCartEnabled = () => {
    if (!selectedProduct || !selectedCart) return false;
    if (selectedProduct.itemType === "CustomMenu") {
      return (selectedProduct as any).customItems?.length > 0;
    }
    return true;
  };

  useEffect(() => { customModalContextRef.current = customModalContext; }, [customModalContext]);
  useEffect(() => { selectedCustomItemRef.current = selectedCustomItem; }, [selectedCustomItem]);

  useEffect(() => {
    if (requestType === "Cart Allocation") {
      const newMap = new Map<string, CartAllocationData>();
      initialCartAllocations?.forEach((cart: any) => {
        newMap.set(cart.cartId, {
          cartId: cart.cartId,
          cartName: cart.cartName,
          allocations: cart.allocations.map((a: any) => ({ ...a })),
          totalAmount: cart.totalAmount,
          totalQuantity: cart.totalQuantity,
        });
      });
      setCartAllocations(newMap);
    }
  }, [initialCartAllocations, requestType]);

  useEffect(() => {
    if (!effectiveRestaurantId) return;
    menuItemTrigger(effectiveRestaurantId)
      .unwrap()
      .then((data: any) => {
        const flat: MenuItem[] = data?.flatMap((cat: any) => cat.items) || [];
        setSingleMenuItemsList(flat.map((it) => ({ ...it, quantity: 0 })));
      })
      .catch(console.error);
  }, [menuItemTrigger, effectiveRestaurantId]);

  const getTierClass = (tier: string) => {
    if (!tier || tier === "No tier") return "step3-tier-standard";
    switch (tier?.toLowerCase()) {
      case "platinum": return "step3-tier-platinum";
      case "gold": return "step3-tier-gold";
      case "silver": return "step3-tier-silver";
      case "bronze": return "step3-tier-bronze";
      default: return "step3-tier-standard";
    }
  };

  const getTierIcon = (tier: string) => {
    if (!tier || tier === "No tier") return "📦";
    switch (tier?.toLowerCase()) {
      case "platinum": return "💎";
      case "gold": return "🥇";
      case "silver": return "🥈";
      case "bronze": return "🥉";
      default: return "📦";
    }
  };

  const getTierDisplay = (tier: string) => (!tier || tier === "No tier" ? "No tier" : tier);

  const getItemTier = (item: any): string => {
    if (item.tags?.class && Array.isArray(item.tags.class) && item.tags.class.length > 0) {
      const v = item.tags.class[0];
      if (v) {
        const n = v.charAt(0).toUpperCase() + v.slice(1).toLowerCase();
        if (["Platinum", "Gold", "Silver", "Bronze"].includes(n)) return n;
      }
    }
    return item.tier || item.class || "No tier";
  };

  useEffect(() => {
    const fetch = async () => {
      if (!effectiveRestaurantId) return;
      setIsLoadingItems(true);
      try {
        const response = await triggerMenuItems({ restaurantId: effectiveRestaurantId, menuType: "All" }).unwrap();
        let allItems: MenuItemOption[] = response
          .filter((item: any) =>
            item.itemType === "SetMenu" ||
            item.itemType === "CustomMenu" ||
            (item.itemType === "MenuItem" && (item.isCustomMenuItem === false || item.isCustomMenuItem === undefined || item.isCustomMenuItem === null)),
          )
          .map((item: any) => ({
            value: item.itemId,
            label: (
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                {item.imageUrl && (
                  <img src={item.imageUrl} alt={item.displayName} width={30} height={30} style={{ borderRadius: 4, objectFit: "cover" }} />
                )}
                <div>
                  <div style={{ fontWeight: 500 }}>{item.displayName}</div>
                  <div style={{ display: "flex", gap: 8, fontSize: 11, color: "#666" }}>
                    {item.price && <span>Rs.{item.price.toFixed(2)}</span>}
                    {item.skuId && <span>SKU: {item.skuId}</span>}
                    {item.itemType === "CustomMenu" && <span style={{ color: "#3b1f6e", fontWeight: 600 }}>⚙ Custom</span>}
                  </div>
                </div>
              </div>
            ),
            displayName: item.displayName,
            tinyId: item.tinyId,
            skuId: item.skuId || "",
            price: item.price,
            itemId: item.itemId,
            productId: item.productId || item.itemId,
            itemType: item.itemType,
            imageUrl: item.imageUrl,
            allergenInformation: item.allergenInformation || [],
            foodAttributes: item.foodAttributes || [],
            isSellingProduct: item.isSellingProduct,
            menuType: "All",
            tier: getItemTier(item),
            _rawItem: item,
          }));
        allItems.sort((a, b) => a.displayName.localeCompare(b.displayName));
        setMenuItemsOptions(allItems);
      } catch (e) {
        console.error(e);
        setItemError("Failed to load menu items.");
      } finally {
        setIsLoadingItems(false);
      }
    };
    fetch();
  }, [triggerMenuItems, effectiveRestaurantId]);

  useEffect(() => {
    if (requestType !== "Cart Allocation" || !effectiveRestaurantId) return;
    setIsLoadingCarts(true);
    triggerCarts(effectiveRestaurantId)
      .unwrap()
      .then((data: any) => {
        const approvedAndCorrectType = data.filter(
          (c: any) => c.cartType === "Fixed" || c.cartType === "fixed" || c.cartType === "moving" || c.cartType === "Moving",
        );
        setCartOptions(
          approvedAndCorrectType.map((cart: any) => ({
            value: cart.id,
            label: (
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <div style={{ fontWeight: 500 }}>{cart.displayName}</div>
                {cart.supportedChannels?.length > 0 && (
                  <div style={{ fontSize: 10, color: "#999" }}>Channels: {cart.supportedChannels.join(", ")}</div>
                )}
              </div>
            ),
            displayName: cart.displayName,
            cartId: cart.id,
            cartType: cart.cartType,
            cartState: cart.cartState,
            supportedChannels: cart.supportedChannels || [],
            location: cart.location,
            owner: cart.owner,
            address: cart.address,
            abbreviation: cart.abbreviation,
          })),
        );
      })
      .catch(console.error)
      .finally(() => setIsLoadingCarts(false));
  }, [triggerCarts, effectiveRestaurantId, requestType]);

  const applyCustomConfig = (context: string, customItems: any[], customItemTotal: number) => {
    if (context === "add") {
      setSelectedProduct((prev) => prev ? ({ ...prev, customItems, customItemTotal } as any) : prev);
    } else if (context.startsWith("pending:")) {
      const [, cartId, itemId] = context.split(":");
      setPendingByCart((prev) => {
        const updated = new Map(prev);
        const rows = (updated.get(cartId) || []).map((r) =>
          r.product.itemId === itemId ? { ...r, customItems, customItemTotal } : r,
        );
        updated.set(cartId, rows);
        return updated;
      });
    } else if (context.startsWith("item:")) {
      const [, itemId] = context.split(":");
      const existing = items.find((i: any) => i.id === itemId);
      if (existing) {
        onRemove(itemId);
        onAdd({ ...existing, customItems, customItemTotal } as any);
      }
    }
  };

const openCustomModal = (product: MenuItemOption, context: string) => {
  const rawItem = (product as any)._rawItem || product;
  const existingCustomItems: any[] = (product as any).customItems || [];
  const existingCustomItemTotal: number = (product as any).customItemTotal ?? product.price ?? 0;

  // ─── Reverse-map saved customItems back to MenuItem collectedItems format ───
  const restoredCollectedItems: any[] = existingCustomItems.map((ci: any) => {
    // Find the matching raw MenuItem from singleMenuItemsList
    const found = singleMenuItemsList.find(
      (m: any) => m.id === (ci.menuItemId || ci.itemId)
    );
    if (found) {
      return {
        ...found,
        itemType: ci.isAddOn ? "GROUP_EXTRA_ITEM" : "SECTION_ITEM",
        isAddOn: ci.isAddOn || false,
        customMenuItemSection: found.customMenuItemSection,
        quantity: ci.quantity ?? 1,
      };
    }
    // Fallback if not found in list
    return {
      id: ci.menuItemId || ci.itemId,
      displayName: ci.displayName,
      price: ci.unitPrice ?? 0,
      itemType: ci.isAddOn ? "GROUP_EXTRA_ITEM" : "SECTION_ITEM",
      isAddOn: ci.isAddOn || false,
      customMenuItemSection: ci.customMenuItemSection || "",
      quantity: ci.quantity ?? 1,
    };
  });

  setSelectedCustomItem(rawItem as MenuItem);
  selectedCustomItemRef.current = rawItem as MenuItem;
  setCustomizedCustomItem({
    availableDays: rawItem.availableDays,
    priceType: rawItem.priceType,
    sections: rawItem.sections || [],
    totalItems: rawItem.totalItems || 0,
    isAvailable: rawItem.isAvailable,
    id: product.itemId,
    displayName: product.displayName,
    price: product.price,
    collectedItems: restoredCollectedItems,
  });

  if (existingCustomItems.length > 0) {
    setModalOrderItems([{
      id: product.itemId,
      customItems: existingCustomItems,
      customItemTotal: existingCustomItemTotal,
    }]);
  } else {
    setModalOrderItems([]);
  }

  setCustomModalContext(context);
  customModalContextRef.current = context;
  setShowCustomModal(true);
};

  const handleCustomModalClose = () => setShowCustomModal(false);

  const interceptSetOrderItems = (updaterOrValue: any) => {
    setModalOrderItems((prev: any[]) => {
      const next: any[] = typeof updaterOrValue === "function" ? updaterOrValue(prev) : updaterOrValue;
      const currentItemId = selectedCustomItemRef.current?.id;
      const produced = next.find((oi: any) => oi.id === currentItemId);
      if (produced) {
        const customItems: any[] = produced.customItems || [];
        const customItemTotal: number = produced.customItemTotal ?? produced.calculatedPrice ?? produced.price ?? 0;
        applyCustomConfig(customModalContextRef.current, customItems, customItemTotal);
      }
      return next;
    });
  };

  const handleAddItem = () => {
    if (!selectedProduct) return;
    if (quantity < 1) { setItemError("Quantity must be at least 1"); return; }
    if (items.find((i: any) => i.id === selectedProduct.itemId)) { setItemError("This item has already been added"); return; }
    if (selectedProduct.itemType === "CustomMenu" && !(selectedProduct as any).customItems?.length) {
      setItemError("Please configure custom items before adding this product.");
      return;
    }
    onAdd({
      id: selectedProduct.itemId,
      name: selectedProduct.displayName,
      skuId: selectedProduct.skuId || "",
      tier: selectedProduct.tier || "",
      qty: quantity,
      price: selectedProduct.price,
      productId: selectedProduct.productId,
      overridePrice: overridePrice ? parseFloat(overridePrice) : undefined,
      imageUrl: selectedProduct.imageUrl,
      tinyId: selectedProduct.tinyId,
      itemType: selectedProduct.itemType,
      allergenInformation: selectedProduct.allergenInformation,
      foodAttributes: selectedProduct.foodAttributes,
      menuType: selectedProduct.menuType,
      customItems: selectedProduct.itemType === "CustomMenu" ? ((selectedProduct as any).customItems ?? []) : undefined,
      customItemTotal: (selectedProduct as any).customItemTotal,
    } as any);
    setSelectedProduct(null);
    setQuantity(1);
    setOverridePrice("");
    setItemError(null);
  };

  const handleAddProductToCart = () => {
    if (!selectedProduct) { setItemError("Please select a product"); return; }
    if (!selectedCart) { setItemError("Please select a cart"); return; }
    if (selectedProduct.itemType === "CustomMenu" && !(selectedProduct as any).customItems?.length) {
      setItemError("Please configure custom items before adding this product to cart.");
      return;
    }
    const existing = pendingByCart.get(selectedCart.cartId) || [];
    if (existing.some((r) => r.product.itemId === selectedProduct.itemId)) {
      setItemError(`${selectedProduct.displayName} is already pending for ${selectedCart.displayName}`);
      return;
    }
    const committed = cartAllocations.get(selectedCart.cartId);
    if (committed?.allocations.some((a) => a.itemId === selectedProduct.itemId)) {
      setItemError(`${selectedProduct.displayName} is already allocated to ${selectedCart.displayName}`);
      return;
    }
    const defaultChannels: Record<string, number> = {};
    (selectedCart.supportedChannels || []).forEach((ch) => { defaultChannels[ch] = 0; });
    setPendingByCart((prev) => {
      const updated = new Map(prev);
      updated.set(selectedCart.cartId, [
        ...(updated.get(selectedCart.cartId) || []),
        { product: selectedProduct, channels: defaultChannels, customItems: (selectedProduct as any).customItems, customItemTotal: (selectedProduct as any).customItemTotal },
      ]);
      return updated;
    });
    setSelectedProduct(null);
    setSelectedCart(null);
    setOverridePrice("");
    setItemError(null);
  };

  const handlePendingChannelChange = (cartId: string, itemId: string, channel: string, value: number) => {
    setPendingByCart((prev) => {
      const updated = new Map(prev);
      const rows = (updated.get(cartId) || []).map((r) =>
        r.product.itemId === itemId ? { ...r, channels: { ...r.channels, [channel]: value } } : r,
      );
      updated.set(cartId, rows);
      return updated;
    });
  };

  const handleRemovePendingRow = (cartId: string, itemId: string) => {
    setPendingByCart((prev) => {
      const updated = new Map(prev);
      const rows = (updated.get(cartId) || []).filter((r) => r.product.itemId !== itemId);
      if (rows.length === 0) updated.delete(cartId);
      else updated.set(cartId, rows);
      return updated;
    });
  };

  const handleCommitCart = (cartId: string) => {
    const rows = pendingByCart.get(cartId) || [];
    if (rows.length === 0) { setItemError("No products to confirm"); return; }
    for (const row of rows) {
      const total = Object.values(row.channels).reduce((s, v) => s + v, 0);
      if (total === 0) { setItemError(`Please allocate at least one unit for "${row.product.displayName}"`); return; }
      if (row.product.itemType === "CustomMenu" && (!row.customItems || row.customItems.length === 0)) {
        setItemError(`Please configure custom items for "${row.product.displayName}" using the ⚙ button`);
        return;
      }
    }
    const cartOpt = cartOptions.find((c) => c.cartId === cartId);
    const cartName = cartOpt?.displayName || cartId;
    const updatedAllocations = new Map(cartAllocations);
    let existingCart = updatedAllocations.get(cartId);
    if (!existingCart) {
      existingCart = { cartId, cartName, allocations: [], totalAmount: 0, totalQuantity: 0 };
    }
    rows.forEach((row) => {
      const productPrice = row.product.price;
      const totalQty = Object.values(row.channels).reduce((s, v) => s + v, 0);
      const existingAllocationIndex = existingCart!.allocations.findIndex((a) => a.itemId === row.product.itemId);
      const newAlloc: any = {
        itemId: row.product.itemId,
        productId: row.product.productId || row.product.itemId,
        productName: row.product.displayName,
        skuId: row.product.skuId || "",
        price: productPrice,
        tier: row.product.tier || "",
        channels: { ...row.channels },
        quantity: totalQty,
        subtotal: productPrice * totalQty,
      };
      if (row.product.itemType === "CustomMenu") {
        newAlloc.itemType = "CustomMenu";
        newAlloc.unitTotalPrice = row.customItemTotal ?? productPrice;
        newAlloc.portionSize = (row.product as any).portionLabel || "";
        newAlloc.perItemManualDiscount = 0;
        newAlloc.customItems = Array.isArray(row.customItems) ? row.customItems : [];
      }
      if (existingAllocationIndex !== -1) {
        const existingAlloc = existingCart!.allocations[existingAllocationIndex];
        for (const [channel, qty] of Object.entries(row.channels)) {
          existingAlloc.channels[channel] = (existingAlloc.channels[channel] || 0) + (qty as number);
        }
        const newTotalQty = Object.values(existingAlloc.channels).reduce((s, v) => s + v, 0);
        existingAlloc.quantity = newTotalQty;
        existingAlloc.subtotal = existingAlloc.price * newTotalQty;
        if (row.product.itemType === "CustomMenu" && row.customItems?.length) {
          existingAlloc.customItems = [...(existingAlloc.customItems || []), ...(row.customItems || [])];
        }
      } else {
        existingCart!.allocations.push(newAlloc);
      }
    });
    existingCart.totalQuantity = existingCart.allocations.reduce((s, a) => s + a.quantity, 0);
    existingCart.totalAmount = existingCart.allocations.reduce((s, a) => s + a.subtotal, 0);
    updatedAllocations.set(cartId, existingCart);
    setPendingByCart((prev) => { const u = new Map(prev); u.delete(cartId); return u; });
    setCartAllocations(updatedAllocations);
    propagateChange(updatedAllocations);
    setItemError(null);
  };

  const handleChannelQuantityChange = (cartId: string, itemId: string, channel: string, value: number) => {
    const updated = new Map(cartAllocations);
    const cart = updated.get(cartId);
    if (!cart) return;
    const idx = cart.allocations.findIndex((a) => a.itemId === itemId);
    if (idx === -1) return;
    cart.allocations[idx].channels[channel] = value;
    const total = Object.values(cart.allocations[idx].channels as Record<string, number>).reduce((s, v) => s + v, 0);
    cart.allocations[idx].quantity = total;
    cart.allocations[idx].subtotal = cart.allocations[idx].price * total;
    cart.totalQuantity = cart.allocations.reduce((s, a) => s + a.quantity, 0);
    cart.totalAmount = cart.allocations.reduce((s, a) => s + a.subtotal, 0);
    updated.set(cartId, cart);
    setCartAllocations(updated);
    propagateChange(updated);
  };

  const handleRemoveFromCart = (cartId: string, itemId: string) => {
    const updated = new Map(cartAllocations);
    const cart = updated.get(cartId);
    if (!cart) return;
    const filtered = cart.allocations.filter((a) => a.itemId !== itemId);
    if (filtered.length === 0) {
      updated.delete(cartId);
    } else {
      cart.allocations = filtered;
      cart.totalQuantity = filtered.reduce((s, a) => s + a.quantity, 0);
      cart.totalAmount = filtered.reduce((s, a) => s + a.subtotal, 0);
      updated.set(cartId, cart);
    }
    setCartAllocations(updated);
    propagateChange(updated);
  };

  const propagateChange = (map: Map<string, CartAllocationData>) => {
    if (!onCartAllocationsChange) return;
    onCartAllocationsChange(
      Array.from(map.values()).map((c) => ({
        cartId: c.cartId,
        cartName: c.cartName,
        allocations: c.allocations.map((a: any) => ({ ...a })),
        totalAmount: c.totalAmount,
        totalQuantity: c.totalQuantity,
      })),
    );
  };

  const channelDisplay = (ch: string) =>
    ch === "WebPickup" ? "WebP" : ch === "WebDelivery" ? "WebD" : ch === "WhatsApp" ? "WApp" : ch;

  const customStyles = {
    control: (p: any) => ({ ...p, minHeight: 42, borderColor: "#e0e0e0", "&:hover": { borderColor: "#3b1f6e" } }),
    option: (p: any, s: any) => ({
      ...p,
      padding: "10px 12px",
      backgroundColor: s.isSelected ? "#3b1f6e" : s.isFocused ? "#f5f0ff" : "white",
      color: s.isSelected ? "white" : "#333",
    }),
  };

  const getAvailableProductsForCart = () => {
    if (!selectedCart) return menuItemsOptions;
    return menuItemsOptions.map((option) => {
      const isAlreadyInThisCart = isAllocatedOrPendingForCart(option.itemId, selectedCart.cartId);
      const isUsedInOtherCart = isProductUsedInAnyCart(option.itemId, selectedCart.cartId);
      return {
        ...option,
        isDisabled: isAlreadyInThisCart,
        label: (
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {option.imageUrl && <img src={option.imageUrl} alt={option.displayName} width={30} height={30} style={{ borderRadius: 4, objectFit: "cover" }} />}
            <div>
              <div style={{ fontWeight: 500 }}>{option.displayName}</div>
              <div style={{ display: "flex", gap: 8, fontSize: 11, color: "#666", flexWrap: "wrap" }}>
                {option.price && <span>Rs.{option.price.toFixed(2)}</span>}
                {option.skuId && <span>SKU: {option.skuId}</span>}
                {option.itemType === "CustomMenu" && <span style={{ color: "#3b1f6e", fontWeight: 600 }}>⚙ Custom</span>}
                {isAlreadyInThisCart && <span style={{ color: "#dc3545", fontWeight: 600 }}>✓ Already added to this cart</span>}
                {isUsedInOtherCart && !isAlreadyInThisCart && <span style={{ color: "#ff9800", fontWeight: 500 }}>📦 Used in other cart</span>}
              </div>
            </div>
          </div>
        ),
      };
    });
  };

  const availableForNonAlloc = menuItemsOptions.filter((i) => !items.some((it: any) => it.id === i.itemId));
  const availableProductsForSelectedCart = getAvailableProductsForCart();
  const allCartIds = new Set([...Array.from(pendingByCart.keys()), ...Array.from(cartAllocations.keys())]);

  // ─── Product Quantity mode ────────────────────────────────────────────────
  if (requestType !== "Cart Allocation") {
    return (
      <div className="step3-container">
        <p className="step3-title">Select Products</p>
        <Row className="mb-3 align-items-end">
          <Col lg={5}>
            <Label className="step3-label">Select Product</Label>
            <Select
              value={selectedProduct}
              onChange={(opt) => { setSelectedProduct(opt); if (opt) { setItemError(null); setQuantity(1); setOverridePrice(""); } }}
              options={availableForNonAlloc}
              placeholder={isLoadingItems ? "Loading products..." : "Search product by name..."}
              isLoading={isLoadingItems}
              filterOption={customFilterOption}
              styles={customStyles}
              className="step3-select"
            />
          </Col>
          <Col lg={2}>
            <Label className="step3-label">Quantity</Label>
            <input
              type="number"
              className="step3-quantity-input"
              value={quantity}
              min={1}
              disabled={!selectedProduct}
              onChange={(e) => { const v = parseInt(e.target.value) || 1; if (v >= 1) setQuantity(v); }}
            />
          </Col>
          {selectedProduct?.itemType === "CustomMenu" && (
            <Col lg={3}>
              <Label className="step3-label">Custom Items</Label>
              <button
                type="button"
                className={`btn w-100 ${(selectedProduct as any).customItems?.length ? "btn-outline-success" : "btn-outline-primary"}`}
                onClick={() => openCustomModal(selectedProduct, "add")}
              >
                <FontAwesomeIcon icon={faSliders} className="me-1" />
                {(selectedProduct as any).customItems?.length ? `✓ Configured (${(selectedProduct as any).customItems.length} items)` : "Configure Custom Items"}
              </button>
            </Col>
          )}
          <Col lg={selectedProduct?.itemType === "CustomMenu" ? 2 : 3} style={{ paddingTop: 4 }}>
            <Label className="step3-label">&nbsp;</Label>
            <button
              onClick={handleAddItem}
              className="step3-add-button w-100"
              disabled={!selectedProduct || (selectedProduct.itemType === "CustomMenu" && !(selectedProduct as any).customItems?.length)}
            >
              Add
            </button>
          </Col>
        </Row>

        {selectedProduct?.itemType === "CustomMenu" && !(selectedProduct as any).customItems?.length && (
          <div className="text-warning small mb-2">⚠ Please click "Configure Custom Items" before adding this product.</div>
        )}
        {itemError && <div className="text-danger mb-2">{itemError}</div>}

        {items.length > 0 && (
          <div className="step3-items-list">
            <h5 className="mt-3 mb-3">Selected Items ({items.length})</h5>
            <table className="table table-bordered" style={{ fontSize: 13 }}>
              <thead>
                <tr><th>Product</th><th>Tier</th><th>Qty</th><th>Price</th><th>Total</th><th>Action</th></tr>
              </thead>
              <tbody>
                {items.map((item: any) => {
                  const price = item.overridePrice || item.price || 0;
                  const isCustom = item.itemType === "CustomMenu";
                  const tierDisplay = getTierDisplay(item.tier);
                  return (
                    <tr key={item.id}>
                      <td>
                        <div style={{ fontWeight: 500 }}>{item.name}</div>
                        {isCustom && (
                          <div style={{ marginTop: 4 }}>
                            {(item as any).customItems?.length
                              ? <span className="text-success" style={{ fontSize: 11 }}>✓ {(item as any).customItems.length} custom item(s)</span>
                              : <span className="text-warning" style={{ fontSize: 11 }}>⚠ Not configured</span>}
                          </div>
                        )}
                      </td>
                      <td><span className={`step3-tier-badge ${getTierClass(item.tier)}`}>{getTierIcon(item.tier)} {tierDisplay}</span></td>
                      <td>{item.qty}</td>
                      <td>Rs. {price.toFixed(2)}</td>
                      <td>Rs. {(price * item.qty).toFixed(2)}</td>
                      <td><button onClick={() => onRemove(item.id)} className="step3-remove-button">×</button></td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="fw-bold bg-light">
                  <td colSpan={2}>Total</td>
                  <td>{items.reduce((s: number, i: any) => s + i.qty, 0)}</td>
                  <td colSpan={2}>Rs. {items.reduce((s: number, i: any) => s + (i.overridePrice || i.price || 0) * i.qty, 0).toFixed(2)}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {showCustomModal && selectedCustomItem && (
          <CustomItemSetupModal
            showModal={showCustomModal}
            handleModalClose={handleCustomModalClose}
            selectedCustomItem={selectedCustomItem}
            setSelectedCustomItem={setSelectedCustomItem}
            singleMenuItemsList={singleMenuItemsList}
            control={{}}
            customizedCustomItem={customizedCustomItem}
            setCustomizedCustomItem={setCustomizedCustomItem}
            onSaveCollectedItems={() => {}}
            orderItems={modalOrderItems}
            setOrderItems={interceptSetOrderItems}
            selectedMenuItemList={[]}
            setSelectedMenuItemList={() => {}}
          />
        )}
      </div>
    );
  }

  // ─── Cart Allocation mode ─────────────────────────────────────────────────
  return (
    <div className="step3-container">
      <p className="step3-title">Allocate Products to Carts</p>

      <div style={{ background: "#f8f9ff", border: "1px solid #dee2e6", borderRadius: 8, padding: 16, marginBottom: 20 }}>
        <Row className="mb-2 align-items-end">
          <Col lg={5}>
            <Label className="step3-label">Select Cart</Label>
            <Select
              value={selectedCart}
              onChange={(opt) => { setSelectedCart(opt); setSelectedProduct(null); if (opt) setItemError(null); }}
              options={cartOptions}
              placeholder={isLoadingCarts ? "Loading carts..." : "Select a cart..."}
              isLoading={isLoadingCarts}
              filterOption={customFilterOption}
              styles={customStyles}
              className="step3-select"
            />
          </Col>
          <Col lg={5}>
            <Label className="step3-label">Select Product</Label>
            <Select
              value={selectedProduct}
              onChange={(opt) => { setSelectedProduct(opt); if (opt) { setItemError(null); setOverridePrice(""); } }}
              options={availableProductsForSelectedCart}
              isOptionDisabled={() => false}
              placeholder={isLoadingItems ? "Loading products..." : "Search product by name..."}
              isLoading={isLoadingItems}
              filterOption={customFilterOption}
              styles={customStyles}
              className="step3-select"
            />
          </Col>
          <Col lg={2} style={{ paddingTop: 4 }}>
            <Label className="step3-label">&nbsp;</Label>
            <button onClick={handleAddProductToCart} className="step3-add-button w-100" disabled={!isAddToCartEnabled()}>
              + Add to Cart
            </button>
          </Col>
        </Row>

        {selectedProduct?.itemType === "CustomMenu" && (
          <Row className="mt-2">
            <Col>
              <button
                type="button"
                className={`btn btn-sm ${(selectedProduct as any).customItems?.length ? "btn-outline-success" : "btn-outline-warning"}`}
                onClick={() => openCustomModal(selectedProduct, "add")}
              >
                <FontAwesomeIcon icon={faSliders} className="me-1" />
                {(selectedProduct as any).customItems?.length
                  ? `✓ Custom Items Configured (${(selectedProduct as any).customItems.length} items)`
                  : "⚠ Configure Custom Items (Required)"}
              </button>
              {!(selectedProduct as any).customItems?.length && (
                <span className="text-warning small ms-2">You must configure custom items before adding to cart.</span>
              )}
            </Col>
          </Row>
        )}
        {itemError && <div className="text-danger small mt-2">{itemError}</div>}
      </div>

      {/* Per-cart sections */}
      {Array.from(allCartIds).map((cartId) => {
        const cartOpt = cartOptions.find((c) => c.cartId === cartId);
        const cartName = cartOpt?.displayName || cartId;
        const channels = cartOpt?.supportedChannels?.length ? cartOpt.supportedChannels : ["WebPickup", "WebDelivery", "WhatsApp"];
        const pendingRows = pendingByCart.get(cartId) || [];
        const committedData = cartAllocations.get(cartId);

        return (
          <div key={cartId} style={{ border: "1px solid #dee2e6", borderRadius: 8, marginBottom: 20, overflow: "hidden" }}>
            <div style={{ background: "linear-gradient(135deg, #3b1f6e 0%, #5c3d9e 100%)", color: "white", padding: "10px 16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <strong>{cartName}</strong>
                <small style={{ marginLeft: 8, opacity: 0.8, fontSize: 11 }}>Channels: {channels.join(", ")}</small>
              </div>
              {committedData && (
                <div style={{ fontSize: 13, opacity: 0.9 }}>
                  Total: Rs. {committedData.totalAmount.toFixed(2)} | Qty: {committedData.totalQuantity}
                </div>
              )}
            </div>

            {pendingRows.length > 0 && (
              <div style={{ padding: "12px 16px", background: "#fffdf0", borderBottom: "1px solid #dee2e6" }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "#856404", marginBottom: 8 }}>⏳ Pending — configure custom items if needed, distribute channel quantities, then confirm</div>
                <table className="table table-sm table-bordered mb-2" style={{ fontSize: 13 }}>
                  <thead style={{ background: "#fff3cd" }}>
                    <tr>
                      <th style={{ minWidth: 200 }}>Product</th>
                      <th style={{ minWidth: 60 }}>Price</th>
                      {channels.map((ch) => <th key={ch} style={{ minWidth: 70, textAlign: "center" }}>{channelDisplay(ch)}</th>)}
                      <th style={{ minWidth: 70, textAlign: "center" }}>Total Qty</th>
                      <th style={{ minWidth: 40 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingRows.map((row) => {
                      const rowTotal = Object.values(row.channels).reduce((s, v) => s + v, 0);
                      return (
                        <tr key={row.product.itemId}>
                          <td>
                            <div style={{ fontWeight: 500 }}>{row.product.displayName}</div>
                            {row.product.skuId && <div style={{ fontSize: 11, color: "#888" }}>SKU: {row.product.skuId}</div>}
                            {row.product.tier && <span className={`step3-tier-badge-small ${getTierClass(row.product.tier)}`} style={{ fontSize: 10 }}>{getTierIcon(row.product.tier)} {getTierDisplay(row.product.tier)}</span>}
                          </td>
                          <td style={{ verticalAlign: "middle" }}>Rs. {row.product.price?.toFixed(2)}</td>
                          {channels.map((ch) => (
                            <td key={ch} style={{ textAlign: "center", verticalAlign: "middle" }}>
                              <input type="number" min="0" value={row.channels[ch] ?? 0}
                                onChange={(e) => handlePendingChannelChange(cartId, row.product.itemId, ch, parseInt(e.target.value) || 0)}
                                style={{ width: 60, textAlign: "center", border: "1px solid #dee2e6", borderRadius: 4, padding: "2px 4px", fontSize: 13 }}
                              />
                            </td>
                          ))}
                          <td style={{ textAlign: "center", verticalAlign: "middle", fontWeight: 600 }}>{rowTotal}</td>
                          <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                            <button onClick={() => handleRemovePendingRow(cartId, row.product.itemId)} style={{ background: "none", border: "none", color: "#dc3545", fontSize: 18, cursor: "pointer", lineHeight: 1 }}>×</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div style={{ fontSize: 12, color: "#666" }}>
                    Pending qty: <strong>{pendingRows.reduce((s, r) => s + Object.values(r.channels).reduce((a, v) => a + v, 0), 0)}</strong>
                    {" | "}Subtotal: <strong>Rs. {pendingRows.reduce((s, r) => s + r.product.price * Object.values(r.channels).reduce((a, v) => a + v, 0), 0).toFixed(2)}</strong>
                  </div>
                  <button onClick={() => handleCommitCart(cartId)} style={{ background: "#28a745", color: "white", border: "none", borderRadius: 6, padding: "6px 18px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                    ✓ Confirm Allocation
                  </button>
                </div>
              </div>
            )}

            {committedData && committedData.allocations.length > 0 && (
              <div style={{ padding: "12px 16px" }}>
                {pendingRows.length > 0 && <div style={{ fontSize: 12, fontWeight: 600, color: "#155724", marginBottom: 8 }}>✅ Confirmed Allocations</div>}
                <table className="table table-sm table-bordered mb-0" style={{ fontSize: 13 }}>
                  <thead style={{ background: "#d4edda" }}>
                    <tr>
                      <th style={{ minWidth: 200 }}>Product</th>
                      <th style={{ minWidth: 60 }}>Price</th>
                      {channels.map((ch) => <th key={ch} style={{ minWidth: 70, textAlign: "center" }}>{channelDisplay(ch)}</th>)}
                      <th style={{ minWidth: 70, textAlign: "center" }}>Total Qty</th>
                      <th style={{ minWidth: 80, textAlign: "right" }}>Subtotal</th>
                      <th style={{ minWidth: 40 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {committedData.allocations.map((alloc: any) => (
                      <tr key={alloc.itemId}>
                        <td>
                          <div style={{ fontWeight: 500 }}>{alloc.productName}</div>
                          {alloc.skuId && <div style={{ fontSize: 11, color: "#888" }}>SKU: {alloc.skuId}</div>}
                          {alloc.tier && <span className={`step3-tier-badge-small ${getTierClass(alloc.tier)}`} style={{ fontSize: 10 }}>{getTierIcon(alloc.tier)} {getTierDisplay(alloc.tier)}</span>}
                          {alloc.itemType === "CustomMenu" && alloc.customItems?.length > 0 && <div style={{ fontSize: 11, color: "#155724", marginTop: 2 }}>✓ {alloc.customItems.length} custom item(s)</div>}
                        </td>
                        <td style={{ verticalAlign: "middle" }}>Rs. {alloc.price.toFixed(2)}</td>
                        {channels.map((ch) => (
                          <td key={ch} style={{ textAlign: "center", verticalAlign: "middle" }}>
                            <input type="number" min="0" value={alloc.channels[ch] ?? 0}
                              onChange={(e) => handleChannelQuantityChange(cartId, alloc.itemId, ch, parseInt(e.target.value) || 0)}
                              style={{ width: 60, textAlign: "center", border: "1px solid #dee2e6", borderRadius: 4, padding: "2px 4px", fontSize: 13 }}
                            />
                          </td>
                        ))}
                        <td style={{ textAlign: "center", verticalAlign: "middle", fontWeight: 600 }}>{alloc.quantity}</td>
                        <td style={{ textAlign: "right", verticalAlign: "middle" }}>Rs. {alloc.subtotal.toFixed(2)}</td>
                        <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                          <button onClick={() => handleRemoveFromCart(cartId, alloc.itemId)} style={{ background: "none", border: "none", color: "#dc3545", fontSize: 18, cursor: "pointer", lineHeight: 1 }}>×</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {pendingRows.length === 0 && (!committedData || committedData.allocations.length === 0) && (
              <div style={{ padding: "12px 16px", color: "#999", fontSize: 13 }}>No products added yet.</div>
            )}
          </div>
        );
      })}

      {cartAllocations.size > 0 && (
        <div style={{ background: "#f8f9ff", border: "1px solid #3b1f6e", borderRadius: 8, padding: "12px 16px" }}>
          <div style={{ display: "flex", gap: 24, flexWrap: "wrap", fontSize: 14 }}>
            <div><span style={{ color: "#666" }}>Total Carts: </span><strong>{cartAllocations.size}</strong></div>
            <div><span style={{ color: "#666" }}>Total Products: </span><strong>{Array.from(cartAllocations.values()).reduce((s, c) => s + c.allocations.length, 0)}</strong></div>
            <div><span style={{ color: "#666" }}>Total Quantity: </span><strong>{Array.from(cartAllocations.values()).reduce((s, c) => s + c.totalQuantity, 0)}</strong></div>
            <div><span style={{ color: "#666" }}>Total Amount: </span><strong style={{ color: "#3b1f6e" }}>Rs. {Array.from(cartAllocations.values()).reduce((s, c) => s + c.totalAmount, 0).toFixed(2)}</strong></div>
          </div>
        </div>
      )}

      {showCustomModal && selectedCustomItem && (
        <CustomItemSetupModal
          showModal={showCustomModal}
          handleModalClose={handleCustomModalClose}
          selectedCustomItem={selectedCustomItem}
          setSelectedCustomItem={setSelectedCustomItem}
          singleMenuItemsList={singleMenuItemsList}
          control={{}}
          customizedCustomItem={customizedCustomItem}
          setCustomizedCustomItem={setCustomizedCustomItem}
          onSaveCollectedItems={() => {}}
          orderItems={modalOrderItems}
          setOrderItems={interceptSetOrderItems}
          selectedMenuItemList={[]}
          setSelectedMenuItemList={() => {}}
        />
      )}
    </div>
  );
};